import { useQuery } from '@tanstack/react-query';
import { TQueryLocation } from 'lib/types/location';
import { HttpService } from 'services/http/http';
import * as Sentry from '@sentry/react';
import { sessionIdService } from 'services/session-id/session-id.service';

const useFallbackLocationQuery = () => {
  const GATEWAY =
    process.env.BOOKING_GEOIP_DOMAIN_NAME ||
    'https://geoip.globalworkandtravel.com';
  const URL = '/api/geoip';

  const getUrl = (path: string): string => {
    return `${GATEWAY}${path}`;
  };

  const httpService = new HttpService({
    options: {
      getUrl,
    },
  });

  return useQuery({
    queryKey: [URL],
    staleTime: Infinity,
    queryFn: async () => {
      try {
        const res = await httpService.get<TQueryLocation>(URL);
        return res;
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            service: 'geoip',
            endpoint: 'api/geoip',
            retry_attempt: 'final',
          },
          extra: {
            gateway: GATEWAY,
            userAgent: navigator.userAgent,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            sessionId: sessionIdService.get(),
          },
          user: {
            id: sessionIdService.get() || 'anonymous',
          },
        });
        throw error;
      }
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error: Error) => {
      Sentry.addBreadcrumb({
        category: 'geoip',
        message: `GeoIP retry failed: ${error.message}`,
        level: 'warning',
        data: {
          retryCount: 3,
          finalFailure: true,
        },
      });
    },
  });
};

export { useFallbackLocationQuery };
